import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, FormControl, Validators } from '@angular/forms';
import {
  SharedModule,
  StringCustomFieldConfig,
  DataService,
  NotificationService
} from '@vendure/admin-ui/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import {
  AboutFormData,
  CardCarouselBannerData,
  CollectionCarouselData,
  CollectionGroupData,
  ContactFormData,
  defaultAboutFormData,
  defaultCardCarouselBannerData,
  defaultCollectionCarouselData,
  defaultCollectionGroupData,
  defaultContactFormData,
  defaultEmailSubscriptionData,
  defaultExternalHtmlContentData,
  defaultFaqListData,
  defaultFeaturedCollectionData,
  defaultHeroSectionData,
  defaultKeyPointListData,
  defaultLayoutOptionData,
  defaultMarketingBannerData,
  defaultPageComponentData,
  defaultProductInfoData,
  defaultProductListingData,
  defaultProductReviewsData,
  defaultSimpleProductInfoData,
  defaultTopReviewsData,
  EmailSubscriptionData,
  ExternalHtmlContentData,
  FaqListData,
  FeaturedCollectionData,
  HeroSectionData,
  KeyPointListData,
  LayoutOptionData,
  MarketingBannerData,
  PageComponentTitleMode,
  ProductInfoData,
  ProductListingData,
  ProductReviewsData,
  SimpleProductInfoData,
  TopReviewsData,
} from '../../ui-types';
import { safeJSONParse } from '../../utils';
import { PageComponentData, PageComponentType } from '../../ui-types';
import { PageType } from '../../ui-types';
import { DEFAULT_ACTIVE_LANGUAGE } from '../../constants';

@Component({
  selector: 'page-component-input',
  templateUrl: './page-component-form.component.html',
  styleUrls: ['../../styles.scss'],
  standalone: true,
  imports: [SharedModule],
})
export class PageComponentInputComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  titleModeControl = new UntypedFormControl();
  sectionIdControl = new FormControl<string>('', [
    Validators.required,
    Validators.pattern(/^[a-z][a-z0-9\-]*$/i), // simple, URL/anchor-friendly id
  ]);
  titleControl = new UntypedFormControl();
  subtitleControl = new UntypedFormControl();
  titleUnderlineControl = new UntypedFormControl();
  textValueDef = {
    type: 'string',
    ui: {
      component: 'text-value-input',
      args: {
        activeLanguageCode: DEFAULT_ACTIVE_LANGUAGE,
        languageChanges$: this.languageChanges$,
      },
    },
  };

  layoutOptionControl = new UntypedFormControl();
  layoutOptionDef = {
    type: 'string',
    ui: {
      component: 'layout-option-input',
      options: {
        forPageComponent: true
      }
    },
  };

  // Overall component type – now the combined data has its own type
  pageComponentTypeControl = new FormControl('', [Validators.required]);

  // === Other (top-level) sections of the combined data ===
  marketingBannerControl = new UntypedFormControl();
  keyPointListControl = new UntypedFormControl();
  cardCarouselBannerControl = new UntypedFormControl();
  heroSectionControl = new UntypedFormControl();
  externalHtmlContentControl = new UntypedFormControl();
  faqListControl = new UntypedFormControl();
  featuredCollectionControl = new UntypedFormControl();
  topReviewsControl = new UntypedFormControl();
  collectionsGroupControl = new UntypedFormControl();
  emailSubscriptionControl = new UntypedFormControl();
  productInfoControl = new UntypedFormControl();
  simpleProductInfoControl = new UntypedFormControl();
  productListingControl = new UntypedFormControl();
  productReviewsControl = new UntypedFormControl();
  collectionCarouselControl = new UntypedFormControl();
  contactFormControl = new UntypedFormControl();
  aboutFormControl = new UntypedFormControl();

  // The combined data object that will be parsed from the JSON string
  pageComponentData: PageComponentData;
  pageType: PageType;
  sectionId: string;
  title: string;
  subtitle: string;
  titleUnderline: boolean;
  private configSubscription: Subscription;
  private collapseAllSubscription: Subscription;
  private isInitializing = true;
  isExpanded = true;
  currentComponentInputDef: any;
  private pageTypeChanges$ = new BehaviorSubject<PageType>(PageType.General);
  private activeLanguageCode = DEFAULT_ACTIVE_LANGUAGE;
  private languageChanges$?: Observable<unknown>;

  constructor(
    private dataService: DataService,
    private changeDetector: ChangeDetectorRef,
    private notificationService: NotificationService
  ) {}

  async ngOnInit() {
    this.pageComponentData = this.normalizePageComponentData(this.formControl.value);
    this.pageType = this.config.ui?.options?.pageType || PageType.General;
    const args = (this.config as any)?.ui?.args ?? {};
    if (typeof args.activeLanguageCode === 'string' && args.activeLanguageCode) {
      this.activeLanguageCode = args.activeLanguageCode;
    }
    if (args?.languageChanges$ && typeof args.languageChanges$.subscribe === 'function') {
      this.languageChanges$ = args.languageChanges$ as Observable<unknown>;
    }
    this.textValueDef = {
      ...this.textValueDef,
      ui: {
        ...this.textValueDef.ui,
        args: {
          activeLanguageCode: this.activeLanguageCode,
          languageChanges$: this.languageChanges$,
        },
      },
    };
    this.pageComponentData.titleMode = (this.pageComponentData.titleMode ?? 'for-section') as PageComponentTitleMode;

    this.sectionId = this.pageComponentData?.sectionId || `section-${this.getSeqNoFromArray()}`;
    this.title = JSON.stringify(this.pageComponentData?.title || { format: 'plain', i18n: {} });
    this.subtitle = JSON.stringify(this.pageComponentData?.subtitle || { format: 'plain', i18n: {} });
    this.titleUnderline = this.pageComponentData?.titleUnderline ?? true;

    this.resetNonCurrentData(this.pageComponentData.type);
    this.currentComponentInputDef = this.makeComponentDef(this.pageComponentData.type);
    this.pageTypeChanges$.next(this.pageType);

    this.initializeFormControls();

    this.configSubscription = this.config.ui?.changes?.subscribe((data) => {
      if (data != null && data !== this.pageType) {
        this.pageType = data;
        this.propagatePageTypeChange();
        this.changeDetector.markForCheck();
      }
    });

    if (this.config.ui?.options?.collapseAll) {
      this.collapseAllSubscription = this.config.ui.options.collapseAll.subscribe(
        (expandState: boolean) => {
          this.isExpanded = expandState;
          this.changeDetector.markForCheck();
        },
      );
    }
  }

  private propagatePageTypeChange(): void {
    if (this.currentComponentInputDef?.ui?.options) {
      this.currentComponentInputDef.ui.options.pageType = this.pageType;
    }
    this.pageTypeChanges$.next(this.pageType);
  }

  async ngAfterViewInit() {
    // Subscribe to overall and section controls
    this.pageComponentTypeControl.valueChanges.subscribe(this.onPageComponentTypeChange);
    this.sectionIdControl.valueChanges.subscribe(this.onSectionIdChange);
    this.titleModeControl.valueChanges.subscribe(this.onTitleModeChange);
    this.titleControl.valueChanges.subscribe(this.onTitleChange);
    this.subtitleControl.valueChanges.subscribe(this.onSubtitleChange);
    this.titleUnderlineControl.valueChanges.subscribe(this.onTitleUnderlineChange);
    this.layoutOptionControl.valueChanges.subscribe(this.onLayoutOptionChange);

    this.marketingBannerControl.valueChanges.subscribe(this.onMarketingBannerChange);
    this.heroSectionControl.valueChanges.subscribe(this.onHeroSectionChange);
    this.keyPointListControl.valueChanges.subscribe(this.onKeyPointListChange);
    this.cardCarouselBannerControl.valueChanges.subscribe(this.onCardCarouselBannerChange);
    this.externalHtmlContentControl.valueChanges.subscribe(this.onExternalHtmlContentChange);
    this.featuredCollectionControl.valueChanges.subscribe(this.onFeaturedCollectionChange);
    this.collectionsGroupControl.valueChanges.subscribe(this.onCollectionsGroupChange);
    this.emailSubscriptionControl.valueChanges.subscribe(this.onEmailSubscriptionChange);
    this.faqListControl.valueChanges.subscribe(this.onFaqListChange);
    this.topReviewsControl.valueChanges.subscribe(this.onTopReviewsChange);
    this.productInfoControl.valueChanges.subscribe(this.onProductInfoChange);
    this.simpleProductInfoControl.valueChanges.subscribe(this.onSimpleProductInfoChange);
    this.productListingControl.valueChanges.subscribe(this.onProductListingChange);
    this.productReviewsControl.valueChanges.subscribe(this.onProductReviewsChange);
    this.collectionCarouselControl.valueChanges.subscribe(this.onCollectionCarouselChange);
    this.contactFormControl.valueChanges.subscribe(this.onContactFormChange);
    this.aboutFormControl.valueChanges.subscribe(this.onAboutFormChange);
    setTimeout(() => {
      this.isInitializing = false;
      this.changeDetector.markForCheck();
    });
  }

  ngOnDestroy(): void {
    if (this.configSubscription) {
      this.configSubscription.unsubscribe();
    }
    if (this.collapseAllSubscription) {
      this.collapseAllSubscription.unsubscribe();
    }
    this.pageTypeChanges$.complete();
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
  }

  private normalizePageComponentData(rawValue: PageComponentData | string | null | undefined): PageComponentData {
    if (!rawValue) {
      return { ...defaultPageComponentData };
    }
    //TODO: remove after migrating/republish all old data
    if (typeof rawValue === 'string') {
      return safeJSONParse<PageComponentData>(
        rawValue,
        this.notificationService,
        defaultPageComponentData,
      ) || { ...defaultPageComponentData };
    }
    return { ...defaultPageComponentData, ...rawValue };
  }

  private getSeqNoFromArray(): number {
    const parent: any = this.formControl?.parent;
    if (!parent || !Array.isArray(parent.controls)) {
      return 1;
    }
    const idx = parent.controls.indexOf(this.formControl);
    return idx === -1 ? 1 : idx + 1;
  }

  private initializeFormControls() {
    // Overall PageComponentData type
    this.pageComponentTypeControl.setValue(this.pageComponentData?.type || '', { emitEvent: false });
    this.sectionIdControl.setValue(this.pageComponentData?.sectionId || `section-${this.getSeqNoFromArray()}`, { emitEvent: false });
    this.titleModeControl.setValue(this.pageComponentData?.titleMode ?? PageComponentTitleMode.ForSection, { emitEvent: false });
    this.titleControl.setValue(JSON.stringify(this.pageComponentData?.title || { format: 'plain', i18n: {} }), { emitEvent: false });
    this.subtitleControl.setValue(JSON.stringify(this.pageComponentData?.subtitle || { format: 'plain', i18n: {} }), { emitEvent: false });
    this.titleUnderlineControl.setValue(this.pageComponentData?.titleUnderline ?? true, { emitEvent: false });
    this.layoutOptionControl.setValue(JSON.stringify(this.pageComponentData?.layoutOption) || '', { emitEvent: false });

    if (this.pageComponentData?.marketingBannerData) {
      this.marketingBannerControl.setValue(JSON.stringify(this.pageComponentData.marketingBannerData || {}), { emitEvent: false });
    }
    if (this.pageComponentData?.heroSection) {
      this.heroSectionControl.setValue(JSON.stringify(this.pageComponentData.heroSection || {}), { emitEvent: false });
    }
    if (this.pageComponentData?.keyPointListData) {
      this.keyPointListControl.setValue(JSON.stringify(this.pageComponentData.keyPointListData || {}), { emitEvent: false });
    }
    if (this.pageComponentData?.cardCarouselBannerData) {
      this.cardCarouselBannerControl.setValue(JSON.stringify(this.pageComponentData.cardCarouselBannerData || {}), { emitEvent: false });
    }
    if (this.pageComponentData?.externalHtmlContent) {
      this.externalHtmlContentControl.setValue(JSON.stringify(this.pageComponentData.externalHtmlContent || {}), { emitEvent: false });
    }
    if (this.pageComponentData?.faqList) {
      this.faqListControl.setValue(JSON.stringify(this.pageComponentData.faqList || {}), { emitEvent: false });
    }
    if (this.pageComponentData?.featuredCollection) {
      this.featuredCollectionControl.setValue(JSON.stringify(this.pageComponentData.featuredCollection || {}), { emitEvent: false });
    }
    if (this.pageComponentData?.collectionGroup) {
      this.collectionsGroupControl.setValue(JSON.stringify(this.pageComponentData.collectionGroup || {}), { emitEvent: false });
    }
    if (this.pageComponentData?.emailSubscription) {
      this.emailSubscriptionControl.setValue(JSON.stringify(this.pageComponentData.emailSubscription || {}), { emitEvent: false });
    }
    if (this.pageComponentData?.productListingInfo) {
      this.productListingControl.setValue(JSON.stringify(this.pageComponentData?.productListingInfo || {}), { emitEvent: false });
    }
    if (this.pageComponentData?.productInfo) {
      this.productInfoControl.setValue(JSON.stringify(this.pageComponentData?.productInfo || {}), { emitEvent: false });
    }
    if (this.pageComponentData?.simpleProductInfo) {
      this.simpleProductInfoControl.setValue(JSON.stringify(this.pageComponentData?.simpleProductInfo || {}), { emitEvent: false });
    }
    if (this.pageComponentData?.topReviews) {
      this.topReviewsControl.setValue(JSON.stringify(this.pageComponentData?.topReviews || {}), { emitEvent: false });
    }
    if (this.pageComponentData?.collectionCarouselData) {
      this.collectionCarouselControl.setValue(JSON.stringify(this.pageComponentData.collectionCarouselData || {}), { emitEvent: false });
    }
    if (this.pageComponentData?.contactFormData) {
      this.contactFormControl.setValue(JSON.stringify(this.pageComponentData.contactFormData || {}), { emitEvent: false });
    }
    if (this.pageComponentData?.productReviews) {
      this.productReviewsControl.setValue(JSON.stringify(this.pageComponentData.productReviews || {}), { emitEvent: false });
    }
    if (this.pageComponentData?.aboutFormData) {
      this.aboutFormControl.setValue(JSON.stringify(this.pageComponentData.aboutFormData || {}), { emitEvent: false });
    }
  }

  private resetNonCurrentData(selectedType: PageComponentType): void {
    // Reset all non-active sections to null or an empty array
    if (selectedType !== 'marketing-banner') {
      this.pageComponentData!.marketingBannerData = undefined;
    }
    if (selectedType !== 'key-point-list') {
      this.pageComponentData!.keyPointListData = undefined;
    }
    if (selectedType != 'card-carousel-banner') {
      this.pageComponentData!.cardCarouselBannerData = undefined;
    }
    if (selectedType !== 'hero-section') {
      this.pageComponentData!.heroSection = undefined;
    }
    if (selectedType !== 'external-html-content') {
      this.pageComponentData!.externalHtmlContent = undefined;
    }
    if (selectedType !== 'faq-list') {
      this.pageComponentData!.faqList = undefined;
    }
    if (selectedType !== 'featured-collection') {
      this.pageComponentData!.featuredCollection = undefined;
    }
    if (selectedType !== 'top-reviews') {
      this.pageComponentData!.topReviews = undefined;
    }
    if (selectedType !== 'collection-group') {
      this.pageComponentData!.collectionGroup = undefined;
    }
    if (selectedType !== 'email-subscription') {
      this.pageComponentData!.emailSubscription = undefined;
    }
    if (selectedType !== 'product-info-section') {
      this.pageComponentData!.productInfo = undefined;
    }
    if (selectedType !== 'simple-product-info-section') {
      this.pageComponentData!.simpleProductInfo = undefined;
    }
    if (selectedType !== 'product-listing-section') {
      this.pageComponentData!.productListingInfo = undefined;
    }
    if (selectedType !== 'collection-carousel') {
      this.pageComponentData!.collectionCarouselData = undefined;
    }
    if (selectedType !== 'contact-form') {
      this.pageComponentData!.contactFormData = undefined;
    }
    if (selectedType !== 'about-form') {
      this.pageComponentData!.aboutFormData = undefined;
    }
    if (selectedType !== 'product-reviews') {
      this.pageComponentData!.productReviews = undefined;
    }
  }

  private makeComponentDef(type: string | null): any {
    const typeToForm: Record<string, string> = {
      [PageComponentType.HeroSection]: 'hero-section-input-form',
      [PageComponentType.MarketingBanner]: 'marketing-banner-form',
      [PageComponentType.KeyPointList]: 'key-point-list-form',
      [PageComponentType.CardCarouselBanner]: 'card-carousel-banner-form',
      [PageComponentType.FaqList]: 'faq-list-form',
      [PageComponentType.ExternalHtmlContent]: 'external-html-content-input',
      [PageComponentType.ContactForm]: 'contact-form',
      [PageComponentType.AboutForm]: 'about-form',
      [PageComponentType.FeaturedCollection]: 'featrued-collection-form',
      [PageComponentType.CollectionGroup]: 'collection-group-input',
      [PageComponentType.EmailSubscription]: 'email-subscription-form',
      [PageComponentType.ProductListingSection]: 'product-listing-section-form',
      [PageComponentType.ProductInfoSection]: 'product-info-section-form',
      [PageComponentType.SimpleProductInfoSection]: 'simple-product-info-section-form',
      [PageComponentType.TopReviews]: 'top-reviews-form',
      [PageComponentType.CollectionCarousel]: 'collection-carousel-form',
      [PageComponentType.ProductReviews]: 'product-reviews-form',
    };

    const typeInputForm = type ? (typeToForm[type] ?? 'external-html-content-input') : 'external-html-content-input';

    return {
      type: 'string',
      ui: {
        component: typeInputForm,
        args: {
          activeLanguageCode: this.activeLanguageCode,
          languageChanges$: this.languageChanges$,
        },
        options: {
          pageType: this.pageType,
          navContext: (this.config?.ui?.options as any)?.navContext,
        },
        changes: this.pageTypeChanges$,
      },
    }
  }

  private onPageComponentTypeChange = (value: string | null) => {
    if (value && this.pageComponentData?.type !== value) {
      this.pageComponentData!.type = value as PageComponentType;
      this.resetNonCurrentData(value as PageComponentType);
      this.currentComponentInputDef = this.makeComponentDef(this.pageComponentData.type);
      this.updateJsonString();
    }
  };

  private onSectionIdChange = (value: string | null) => {
    if (value && this.pageComponentData?.sectionId !== value) {
      this.pageComponentData!.sectionId = value;
      this.updateJsonString();
    }
  };

  private onTitleModeChange = (value: PageComponentTitleMode | null) => {
    if (value && this.pageComponentData?.titleMode !== value) {
      this.pageComponentData!.titleMode = value;
      this.updateJsonString();
    }
  };

  private onTitleChange = (value: string | null) => {
    if (value === null) {
      return;
    }
    const nextValue = this.parseTextValueControl(value);
    if (JSON.stringify(this.pageComponentData?.title) !== JSON.stringify(nextValue)) {
      this.pageComponentData!.title = nextValue;
      this.updateJsonString();
    }
  };

  private onSubtitleChange = (value: string | null) => {
    if (value === null) {
      return;
    }
    const nextValue = this.parseTextValueControl(value);
    if (JSON.stringify(this.pageComponentData?.subtitle) !== JSON.stringify(nextValue)) {
      this.pageComponentData!.subtitle = nextValue;
      this.updateJsonString();
    }
  };

  private parseTextValueControl(raw: string | null | undefined) {
    if (!raw) {
      return { format: 'plain', i18n: {} };
    }
    try {
      return JSON.parse(raw);
    } catch {
      return { format: 'plain', i18n: {} };
    }
  }

  private onTitleUnderlineChange = (value: boolean | null) => {
    if (value !== null && this.pageComponentData?.titleUnderline !== value) {
      this.pageComponentData!.titleUnderline = value;
      this.updateJsonString();
    }
  };

  private onLayoutOptionChange = (value: string | null) => {
    if (value && JSON.stringify(this.pageComponentData?.layoutOption) !== value) {
      this.pageComponentData!.layoutOption = safeJSONParse<LayoutOptionData>(value, this.notificationService, defaultLayoutOptionData);
      this.updateJsonString();
    }
  };

  private onMarketingBannerChange = (value: string) => {
    if (JSON.stringify(this.pageComponentData?.marketingBannerData) !== value) {
      this.pageComponentData!.marketingBannerData = safeJSONParse<MarketingBannerData>(value, this.notificationService, defaultMarketingBannerData);
      this.updateJsonString();
    }
  };

  private onKeyPointListChange = (value: string) => {
    if (JSON.stringify(this.pageComponentData?.keyPointListData) !== value) {
      this.pageComponentData!.keyPointListData = safeJSONParse<KeyPointListData>(value, this.notificationService, defaultKeyPointListData);
      this.updateJsonString();
    }
  };

  private onCardCarouselBannerChange = (value: string) => {
    if (JSON.stringify(this.pageComponentData?.cardCarouselBannerData) !== value) {
      this.pageComponentData!.cardCarouselBannerData = safeJSONParse<CardCarouselBannerData>(value, this.notificationService, defaultCardCarouselBannerData);
      this.updateJsonString();
    }
  };

  private onHeroSectionChange = (value: string) => {
    if (this.pageComponentData && JSON.stringify(this.pageComponentData.heroSection) !== value) {
      this.pageComponentData!.heroSection = safeJSONParse<HeroSectionData>(value, this.notificationService, defaultHeroSectionData);
      this.updateJsonString();
    }
  };

  private onExternalHtmlContentChange = (value: string) => {
    if (JSON.stringify(this.pageComponentData?.externalHtmlContent) !== value) {
      this.pageComponentData!.externalHtmlContent = safeJSONParse<ExternalHtmlContentData>(value, this.notificationService, defaultExternalHtmlContentData);
      this.updateJsonString();
    }
  };

  private onFaqListChange = (value: string) => {
    if (JSON.stringify(this.pageComponentData?.faqList) !== value) {
      this.pageComponentData!.faqList = safeJSONParse<FaqListData>(value, this.notificationService, defaultFaqListData);
      this.updateJsonString();
    }
  };

  private onFeaturedCollectionChange = (value: string) => {
    if (JSON.stringify(this.pageComponentData?.featuredCollection) !== value) {
      this.pageComponentData!.featuredCollection = safeJSONParse<FeaturedCollectionData>(value, this.notificationService, defaultFeaturedCollectionData);
      this.updateJsonString();
    }
  };

  private onTopReviewsChange = (value: string) => {
    if (JSON.stringify(this.pageComponentData?.topReviews) !== value) {
      this.pageComponentData!.topReviews = safeJSONParse<TopReviewsData>(value, this.notificationService, defaultTopReviewsData);
      this.updateJsonString();
    }
  };

  private onProductReviewsChange = (value: string) => {
    if (JSON.stringify(this.pageComponentData?.productReviews) !== value) {
      this.pageComponentData!.productReviews = safeJSONParse<ProductReviewsData>(value, this.notificationService, defaultProductReviewsData);
      this.updateJsonString();
    }
  };

  private onCollectionsGroupChange = (value: string) => {
    if (JSON.stringify(this.pageComponentData?.collectionGroup) !== value) {
      this.pageComponentData!.collectionGroup = safeJSONParse<CollectionGroupData>(value, this.notificationService, defaultCollectionGroupData);
      this.updateJsonString();
    }
  };

  private onEmailSubscriptionChange = (value: string) => {
    if (JSON.stringify(this.pageComponentData?.emailSubscription) !== value) {
      this.pageComponentData!.emailSubscription = safeJSONParse<EmailSubscriptionData>(value, this.notificationService, defaultEmailSubscriptionData);
      this.updateJsonString();
    }
  };

  private onProductInfoChange = (value: string) => {
    if (JSON.stringify(this.pageComponentData?.productInfo) !== value) {
      this.pageComponentData!.productInfo = safeJSONParse<ProductInfoData>(value, this.notificationService, defaultProductInfoData);
      this.updateJsonString();
    }
  };

  private onSimpleProductInfoChange = (value: string) => {
    if (JSON.stringify(this.pageComponentData?.simpleProductInfo) !== value) {
      this.pageComponentData!.simpleProductInfo = safeJSONParse<SimpleProductInfoData>(value, this.notificationService, defaultSimpleProductInfoData);
      this.updateJsonString();
    }
  };

  private onProductListingChange = (value: string) => {
    if (JSON.stringify(this.pageComponentData?.productListingInfo) !== value) {
      this.pageComponentData!.productListingInfo = safeJSONParse<ProductListingData>(value, this.notificationService, defaultProductListingData);
      this.updateJsonString();
    }
  };

  private onCollectionCarouselChange = (value: string) => {
    if (JSON.stringify(this.pageComponentData?.collectionCarouselData) !== value) {
      this.pageComponentData!.collectionCarouselData = safeJSONParse<CollectionCarouselData>(value, this.notificationService, defaultCollectionCarouselData);
      this.updateJsonString();
    }
  };

  private onContactFormChange = (value: string) => {
    if (JSON.stringify(this.pageComponentData?.contactFormData) !== value) {
      this.pageComponentData!.contactFormData = safeJSONParse<ContactFormData>(value, this.notificationService, defaultContactFormData);
      this.updateJsonString();
    }
  };

  private onAboutFormChange = (value: string) => {
    if (JSON.stringify(this.pageComponentData?.aboutFormData) !== value) {
      this.pageComponentData!.aboutFormData = safeJSONParse<AboutFormData>(value, this.notificationService, defaultAboutFormData);
      this.updateJsonString();
    }
  };

  private updateJsonString(markDirty: boolean = true) {
    // Clean unused data before converting it to JSON.
    const cleanedData = this.cleanPageComponentData();
    this.formControl.setValue(cleanedData as PageComponentData, { emitEvent: !this.isInitializing });
    if (markDirty && !this.isInitializing) {
      this.formControl.markAsDirty();
    }
  }

  private cleanPageComponentData(): Partial<PageComponentData> {
    const data: Partial<PageComponentData> = { ...this.pageComponentData };
    Object.keys(data).forEach((key) => {
      const value = data[key];
      if (value === null || (Array.isArray(value) && value.length === 0)) {
        delete data[key];
      }
    });
    return data;
  }
}
